import React, { useEffect, useState } from 'react';
import { Planta, createPlanta, updatePlanta } from '../services/plantaService';
import { getClientes, Cliente } from '../services/clienteService';
import { Button, FormField, Input, Modal } from './ui';

interface PlantaFormProps {
  planta: Planta;
  onPlantaSaved: () => void;
  onCancel: () => void;
  /** Nombre del cliente sobre el que se inició el alta, para mostrarlo fijo. */
  nombreCliente?: string;
}

const PlantaForm: React.FC<PlantaFormProps> = ({ planta, onPlantaSaved, onCancel, nombreCliente }) => {
  // Sin id se está creando una planta nueva; con id se edita la existente.
  const esNueva = !planta.id;

  const [formData, setFormData] = useState<Planta>({
    id: planta.id,
    id_cliente: planta.id_cliente,
    denominacion: planta.denominacion || '',
    direccion: planta.direccion || '',
  });
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // El cliente ya viene definido por la ficha desde la que se abrió el alta;
    // la lista sólo hace falta para mostrar su nombre si no llegó por props.
    if (nombreCliente) return;
    getClientes()
      .then(setClientes)
      .catch(err => {
        console.error('Error al cargar clientes:', err);
        setError('Error al cargar la lista de clientes');
      });
  }, [nombreCliente]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.id_cliente) {
      setError('La planta tiene que pertenecer a un cliente.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const datos = {
        denominacion: formData.denominacion,
        direccion: formData.direccion,
        id_cliente: formData.id_cliente,
      };
      const guardada = esNueva ? await createPlanta(datos) : await updatePlanta(formData.id!, datos);
      if (!guardada) {
        setError(esNueva ? 'No se pudo crear la planta.' : 'No se pudo actualizar la planta.');
        return;
      }
      // Al guardar se cierra la ventana y la lista de atrás se recarga.
      onPlantaSaved();
    } catch (err) {
      console.error('Error al guardar la planta:', err);
      setError(esNueva ? 'No se pudo crear la planta.' : 'No se pudo actualizar la planta.');
    } finally {
      setLoading(false);
    }
  };

  const nombreDelCliente =
    nombreCliente || clientes.find(c => c.id === formData.id_cliente)?.nombre || `Cliente #${formData.id_cliente}`;

  return (
    <Modal
      title={esNueva ? 'Crear Planta' : 'Editar Planta'}
      onClose={onCancel}
      size="sm"
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="planta-form" loading={loading}>
            {esNueva ? 'Crear planta' : 'Guardar'}
          </Button>
        </div>
      }
    >
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form id="planta-form" onSubmit={handleSubmit} className="space-y-4">
        <FormField label="Denominación" htmlFor="denominacion" required>
          <Input
            type="text"
            id="denominacion"
            name="denominacion"
            value={formData.denominacion}
            onChange={handleChange}
            placeholder="Nombre de la planta"
            required
          />
        </FormField>

        <FormField label="Dirección" htmlFor="direccion">
          <Input
            type="text"
            id="direccion"
            name="direccion"
            value={formData.direccion}
            onChange={handleChange}
          />
        </FormField>

        {/* El cliente sale de la ficha desde la que se abrió el alta y no se elige. */}
        <FormField label="Cliente Asociado" htmlFor="id_cliente">
          <Input type="text" id="id_cliente" value={nombreDelCliente} disabled readOnly />
        </FormField>
      </form>
    </Modal>
  );
};

export default PlantaForm;
